import { baseApi } from 'lib/rtkQuery/baseApi';
import { QueryTags } from 'lib/rtkQuery/constants';
import { invalidatesTags } from 'lib/rtkQuery/helpers';
import { ListQueryResponse, QueryResponse } from 'lib/rtkQuery/types';
import {
  ListUserDetailQueryResponse,
  TGetUserListParams,
  TUpdateCurrentUserPasswordPayload,
  TUpdateCurrentUserPayload,
  TUpdatePasswordPayload,
  TUpdateUserAccountPayload,
  TUpdateUserPayload,
  TUpdateUserRolePayload,
  TUpdateUserStatus,
  TUser,
  TUserAccount,
  TUserAccountsParams,
  TUserListItem,
} from './types';

const endpoint = '/users';

export const userApi = baseApi
  .enhanceEndpoints({
    addTagTypes: [QueryTags.UserAccounts],
  })
  .injectEndpoints({
    endpoints: builder => ({
      GetUserAccounts: builder.query<
        QueryResponse<TUserAccount[]>,
        TUserAccountsParams
      >({
        query: (params: TUserAccountsParams) => ({
          url: `${endpoint}/Search`,
          method: 'GET',
          params,
        }),
        providesTags: [QueryTags.UserAccounts],
      }),

      LookUpUser: builder.query<QueryResponse<TUser[]>, string>({
        query: (keyword: string) => ({
          url: `${endpoint}/Lookup`,
          method: 'GET',
          params: { keyword },
        }),
      }),

      GetUserDetail: builder.query<QueryResponse<TUserAccount>, string>({
        query: (id: string) => ({
          url: `${endpoint}/Get`,
          method: 'GET',
          params: { id },
        }),
      }),

      AddEditUserAccount: builder.mutation<
        QueryResponse<TUserAccount>,
        TUpdateUserAccountPayload
      >({
        query: (payload: TUpdateUserAccountPayload) => ({
          url: `${endpoint}/${payload?.id ? 'Update' : 'Create'}`,
          method: payload?.id ? 'PATCH' : 'POST',
          data: payload,
        }),
        invalidatesTags: invalidatesTags([QueryTags.UserAccounts]),
      }),

      UpdatePassword: builder.mutation<
        QueryResponse<string>,
        TUpdatePasswordPayload
      >({
        query: (params: TUpdatePasswordPayload) => ({
          url: `${endpoint}/UpdatePassword`,
          method: 'PUT',
          data: params,
        }),
      }),

      GetUserDetailList: builder.query<
        ListUserDetailQueryResponse<TUserListItem[]>,
        void
      >({
        query: () => ({
          url: `${endpoint}/user-details`,
        }),
      }),

      UpdateCurrentUser: builder.mutation<
        QueryResponse<TUserListItem>,
        TUpdateCurrentUserPayload
      >({
        query: (payload: TUpdateCurrentUserPayload) => ({
          url: `${endpoint}/current-user`,
          method: 'PUT',
          data: payload,
        }),
        invalidatesTags: invalidatesTags([QueryTags.UserDetail]),
      }),

      UpdateCurrentUserPassword: builder.mutation<
        QueryResponse<string>,
        TUpdateCurrentUserPasswordPayload
      >({
        query: payload => ({
          url: `${endpoint}/current-user/password`,
          method: 'PATCH',
          data: payload,
        }),
      }),
      GetUserList: builder.query<
        ListQueryResponse<TUser[]>,
        TGetUserListParams
      >({
        query: params => ({
          url: `${endpoint}`,
          method: 'GET',
          params,
        }),
        providesTags: [QueryTags.UserAccounts],
      }),

      UpdateUser: builder.mutation<QueryResponse<void>, TUpdateUserPayload>({
        query: ({ userCode, userInfo }) => ({
          url: `${endpoint}/${userCode}`,
          method: 'PUT',
          data: userInfo,
        }),
        invalidatesTags: invalidatesTags([QueryTags.UserAccounts]),
      }),

      UpdateUserRole: builder.mutation<
        QueryResponse<void>,
        TUpdateUserRolePayload
      >({
        query: ({ userCode, rolesArr }) => ({
          url: `${endpoint}/${userCode}/roles`,
          method: 'PATCH',
          data: { role_name: rolesArr },
        }),
        invalidatesTags: invalidatesTags([QueryTags.UserAccounts]),
      }),
      UpdateUserStatus: builder.mutation<
        QueryResponse<void>,
        TUpdateUserStatus
      >({
        query: ({ userCode, status }) => ({
          url: `${endpoint}/${userCode}`,
          method: 'PATCH',
          data: { status },
        }),
        invalidatesTags: invalidatesTags([QueryTags.UserAccounts]),
      }),
    }),
  });

export const {
  useGetUserAccountsQuery,
  useLookUpUserQuery,
  useGetUserDetailQuery,
  useAddEditUserAccountMutation,
  useUpdatePasswordMutation,
  useGetUserDetailListQuery,
  useUpdateCurrentUserMutation,
  useUpdateCurrentUserPasswordMutation,
  useGetUserListQuery,
  useUpdateUserStatusMutation,
  useUpdateUserMutation,
  useUpdateUserRoleMutation,
} = userApi;
